
import React, { useState } from 'react';
import { Ticket, InventoryItem, LabelSize, Employee } from '../types';
import { Button } from './Button';
import { PrintableDeviceLabel } from './PrintableDeviceLabel';
import { PrintableInventoryLabel } from './PrintableInventoryLabel';
import { CustomLabelDesigner } from './CustomLabelDesigner';
import { DYMO_LABEL_SIZES, DEFAULT_LABEL_SIZE_ID } from '../constants';

interface LabelsPageProps {
  tickets: Ticket[];
  inventoryItems: InventoryItem[];
  onOpenPrintModal: (title: string, content: React.ReactNode, contentClassName?: string) => void;
  currentUser: Employee | null;
}

const TagIconSolid: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 mr-2 text-[var(--color-primary)]" {...props}>
    <path fillRule="evenodd" d="M4.5 3.75a3 3 0 00-3 3v10.5a3 3 0 003 3h15a3 3 0 003-3V6.75a3 3 0 00-3-3h-15zm4.125 3a2.25 2.25 0 100 4.5 2.25 2.25 0 000-4.5zM12 12.75a.75.75 0 000 1.5h5.25a.75.75 0 000-1.5H12z" clipRule="evenodd" />
    <path d="M4.125 9.75a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5z" />
  </svg>
);


export const LabelsPage: React.FC<LabelsPageProps> = ({ tickets, inventoryItems, onOpenPrintModal, currentUser }) => {
  const [selectedTicketId, setSelectedTicketId] = useState<string>('');
  const [selectedInventoryId, setSelectedInventoryId] = useState<string>('');
  const [selectedDeviceLabelSizeId, setSelectedDeviceLabelSizeId] = useState<string>(DEFAULT_LABEL_SIZE_ID);
  const [selectedInventoryLabelSizeId, setSelectedInventoryLabelSizeId] = useState<string>(DEFAULT_LABEL_SIZE_ID);


  const selectedTicket = tickets.find(t => t.id === selectedTicketId);
  const selectedInventoryItem = inventoryItems.find(item => item.id === selectedInventoryId);

  const handlePrintDeviceLabel = () => {
    if (selectedTicket) {
      const labelSize = DYMO_LABEL_SIZES.find(s => s.id === selectedDeviceLabelSizeId) || DYMO_LABEL_SIZES[0];
      onOpenPrintModal(
        `Device Label - ${selectedTicket.id}`,
        <PrintableDeviceLabel ticket={selectedTicket} labelSizeId={selectedDeviceLabelSizeId} />,
        `label-print-base ${labelSize.printClassName}`
      );
    }
  };

  const handlePrintInventoryLabel = () => {
    if (selectedInventoryItem) {
      const labelSize = DYMO_LABEL_SIZES.find(s => s.id === selectedInventoryLabelSizeId) || DYMO_LABEL_SIZES[0];
      onOpenPrintModal(
        `Inventory Label - ${selectedInventoryItem.sku || selectedInventoryItem.name}`,
        <PrintableInventoryLabel item={selectedInventoryItem} labelSizeId={selectedInventoryLabelSizeId} />,
        `label-print-base ${labelSize.printClassName}`
      );
    }
  };
  
  const commonSelectClass = "w-full max-w-md px-3 py-2 border border-slate-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] sm:text-sm";

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex items-center mb-1">
          <TagIconSolid />
          <h1 className="text-2xl font-bold text-[var(--color-primary)]">Generate Labels</h1>
        </div>
        <p className="text-sm text-slate-600">Select a ticket or inventory item to generate a printable label, or design a custom label.</p>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold text-slate-700 mb-4">Device Label</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="ticket-select" className="block text-sm font-medium text-slate-700 mb-1">
              Select Ticket:
            </label>
            <select
              id="ticket-select"
              value={selectedTicketId}
              onChange={(e) => setSelectedTicketId(e.target.value)}
              className={commonSelectClass}
              aria-label="Select ticket for device label"
            >
              <option value="">-- Select a Ticket --</option>
              {tickets.map(ticket => (
                <option key={ticket.id} value={ticket.id}>
                  {ticket.id} - {ticket.customer} - {ticket.subject}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="device-label-size" className="block text-sm font-medium text-slate-700 mb-1">
              Label Size:
            </label>
            <select
              id="device-label-size"
              value={selectedDeviceLabelSizeId}
              onChange={(e) => setSelectedDeviceLabelSizeId(e.target.value)}
              className={commonSelectClass}
              aria-label="Select device label size"
            >
              {DYMO_LABEL_SIZES.map(size => (
                <option key={size.id} value={size.id}>{size.name}</option>
              ))}
            </select>
          </div>
        </div>

        {selectedTicket && (
          <div className="my-4 p-4 border border-slate-200 rounded-md bg-slate-50 max-w-md">
            <h3 className="font-semibold text-slate-700">Selected Ticket Details:</h3>
            <PrintableDeviceLabel ticket={selectedTicket} labelSizeId={selectedDeviceLabelSizeId} />
          </div>
        )}

        <Button onClick={handlePrintDeviceLabel} disabled={!selectedTicket} variant="primary">
          Generate & Print Device Label
        </Button>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold text-slate-700 mb-4">Inventory Item Label</h2>
         <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="inventory-select" className="block text-sm font-medium text-slate-700 mb-1">
              Select Inventory Item:
            </label>
            <select
              id="inventory-select"
              value={selectedInventoryId}
              onChange={(e) => setSelectedInventoryId(e.target.value)}
              className={commonSelectClass}
              aria-label="Select item for inventory label"
            >
              <option value="">-- Select an Inventory Item --</option>
              {inventoryItems.map(item => (
                <option key={item.id} value={item.id}>
                  {item.sku ? `${item.sku} - ` : ''}{item.name} (Stock: {item.stock}) ({item.source})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="inventory-label-size" className="block text-sm font-medium text-slate-700 mb-1">
              Label Size:
            </label>
            <select
              id="inventory-label-size"
              value={selectedInventoryLabelSizeId}
              onChange={(e) => setSelectedInventoryLabelSizeId(e.target.value)}
              className={commonSelectClass}
              aria-label="Select inventory label size"
            >
              {DYMO_LABEL_SIZES.map(size => (
                <option key={size.id} value={size.id}>{size.name}</option>
              ))}
            </select>
          </div>
        </div>

        {selectedInventoryItem && (
          <div className="my-4 p-4 border border-slate-200 rounded-md bg-slate-50 max-w-md">
            <h3 className="font-semibold text-slate-700">Selected Item Details:</h3>
             <PrintableInventoryLabel item={selectedInventoryItem} labelSizeId={selectedInventoryLabelSizeId} />
          </div>
        )}

        <Button onClick={handlePrintInventoryLabel} disabled={!selectedInventoryItem} variant="primary">
          Generate & Print Inventory Label
        </Button>
      </div>

      {/* Custom Label Designer Section */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold text-slate-700 mb-4">Custom Label Designer</h2>
        <CustomLabelDesigner onOpenPrintModal={onOpenPrintModal} />
      </div>

    </div>
  );
};